import { parse } from 'tldts';

import { fromExactSubdomainRegex, toSubdomainRegex } from './subdomainRegex.helper';

import type { AppSitePublishersConfiguration } from '@/contexts/Targeting/TargetingContext';
import type { ClassifiedGenericEntries, InventoryOperation } from '../../AppAndSiteMarketplace.types';

type AddGenericEntriesResult = {
  domains: string[];
  /** Subdomains that did not fit in the list because of the limit. */
  skippedSubdomains: number;
  subdomains: string[];
};

type ApplyGenericEntriesResult = {
  /** Configuration keys to update (domains and subdomains lists of the operation). */
  patch: AppSitePublishersConfiguration;
  /** Subdomains that did not fit in the list because of the limit. */
  skippedSubdomains: number;
};

export const MAX_SUBDOMAINS_PER_LIST = 100;

const uniq = (items: string[]): string[] => [...new Set(items)];

/** Splits entries into domains, subdomains and invalid ones. Deduplicated, keeping the input order. */
export const classifyGenericEntries = (entries: string[]): ClassifiedGenericEntries => {
  const domains: string[] = [];
  const subdomains: string[] = [];
  const invalid: string[] = [];

  for (const entry of entries) {
    const { domain, hostname, isIcann, isIp, isPrivate, subdomain } = parse(entry);
    if (!domain || !hostname || isIp || (!isIcann && !isPrivate)) {
      invalid.push(entry);
    } else if (subdomain === '' || subdomain === 'www') {
      domains.push(domain);
    } else {
      subdomains.push(hostname.replace(/^www\./, ''));
    }
  }

  return { domains: uniq(domains), invalid, subdomains: uniq(subdomains) };
};

/** Adds the classified entries to the current lists. Subdomains are capped at `limit` per list. */
export const addGenericEntries = (
  currentDomains: string[],
  currentSubdomains: string[],
  classified: Pick<ClassifiedGenericEntries, 'domains' | 'subdomains'>,
  limit: number = MAX_SUBDOMAINS_PER_LIST
): AddGenericEntriesResult => {
  const knownSubdomains = new Set(currentSubdomains);
  // Subdomains are stored as regex; a legacy plain hostname or an exact-match regex (V1 or V2) of the same hostname
  // already in the list counts as a duplicate too. V1 contains / starts with / ends with regexes do not.
  const knownHostnames = new Set(currentSubdomains.map(value => fromExactSubdomainRegex(value)?.toLowerCase()));
  const newSubdomains = uniq(
    classified.subdomains
      .filter(hostname => !knownSubdomains.has(hostname) && !knownHostnames.has(hostname))
      .map(toSubdomainRegex)
      .filter(regex => !knownSubdomains.has(regex))
  );
  const room = Math.max(limit - currentSubdomains.length, 0);

  return {
    domains: uniq([...currentDomains, ...classified.domains]),
    skippedSubdomains: Math.max(newSubdomains.length - room, 0),
    subdomains: [...currentSubdomains, ...newSubdomains.slice(0, room)]
  };
};

/** Adds the classified entries to the include/exclude generic lists of the current configuration. */
export const applyGenericEntries = (
  config: AppSitePublishersConfiguration,
  operation: InventoryOperation,
  classified: Pick<ClassifiedGenericEntries, 'domains' | 'subdomains'>
): ApplyGenericEntriesResult => {
  const isInclude = operation === 'include';
  const domainsKey = isInclude ? 'whitelistedGenericPublishers' : 'blacklistedGenericPublishers';
  const subdomainsKey = isInclude ? 'whitelistedPublishers' : 'blacklistedPublishers';
  const next = addGenericEntries(config[domainsKey] ?? [], config[subdomainsKey] ?? [], classified);

  return {
    patch: { [domainsKey]: next.domains, [subdomainsKey]: next.subdomains },
    skippedSubdomains: next.skippedSubdomains
  };
};
